<script lang="ts">
    import { untrack } from 'svelte';
    import { fade } from 'svelte/transition';
    import type { TileImage } from '@shared/types/types';
    import { getImageUrl } from '@tile/components/product-image/ImageUrlBuilder.ts';
    import type { ImageAspectRatio, ImageLoadingOptions } from '@tile/components/product-image/types.ts';

    interface Props {
        image: TileImage;
        imageLoadingOptions: ImageLoadingOptions;
        variationId: string;
        size?: { width: string; height: string };
        aspectRatio?: ImageAspectRatio;
    }

    let { image, variationId, imageLoadingOptions, size, aspectRatio }: Props = $props();

    const imagePaddingTop = $derived.by(() => {
        if (!aspectRatio) return '100%';

        switch (aspectRatio) {
            case 'landscape':
            case 'landscape-two-column':
                return '69.5%';
            case 'portrait':
                return '143%';
            default:
                return '100%';
        }
    });

    /**
 *
 *
 *
 */
    const shouldCut = $derived.by(() => {
        switch (image.cuttable) {
            case 'PORTRAIT':
                return aspectRatio === 'portrait';
            case 'LANDSCAPE':
            case 'SQUARE':
                return aspectRatio === 'landscape' || aspectRatio === 'landscape-two-column' || aspectRatio === 'quad';
            default:
                return false;
        }
    });

    let loadingError = $state(false);

    const useDimPreset = $derived(image.grayBackground === true);
    const webpSrc = $derived(getImageUrl(image.id ?? '', 'webp', aspectRatio, useDimPreset));
    const jpegSrc = $derived(getImageUrl(image.id ?? '', 'jpeg', aspectRatio, useDimPreset));
    const imgSrc = $derived(loadingError ? (image.fallback ?? '') : jpegSrc);

    /**
 *
 *
 */
    let lastVariationId = $state(untrack(() => variationId));
    $effect(() => {
        if (variationId !== lastVariationId) {
            loadingError = false;
            lastVariationId = variationId;
        }
    });
</script>

<div
    class="reptile-product-image"
    style:width={size?.width}
    style:height={size?.height}
    style:padding-top={imagePaddingTop}
>
    <div class="reptile-product-image__frame" data-qa={`ftfind-product-image-${variationId}`}>
        {#key variationId}
            <picture
                class="reptile-product-image__picture"
                data-qa={useDimPreset ? 'ftfind-product-image-gray-background' : undefined}
                in:fade
            >
                {#if !loadingError}
                    <source srcset={webpSrc} type="image/webp" />
                    <source srcset={jpegSrc} type="image/jpeg" />
                {/if}
                <img
                    class={['reptile-product-image__image', { 'reptile-product-image__image--cut': shouldCut }]}
                    data-qa={shouldCut ? 'ftfind-product-image-cropped' : undefined}
                    src={imgSrc}
                    alt={image.alt ?? ''}
                    onerror={() => {
                        loadingError = true;
                    }}
                    loading={imageLoadingOptions.loading}
                    fetchpriority={imageLoadingOptions.fetchpriority}
                />
            </picture>
        {/key}
    </div>
</div>

<style lang="scss">
    @use '@otto-ec/design-tokens/tokens';

    .reptile-product-image {
        position: relative;

        &__frame {
            user-select: none;

            position: absolute;
            inset: 0;

            overflow: hidden;
            display: block;

            width: 100%;
            border-radius: tokens.$oc-semantic-border-radius-25;

            background-color: #f5f5f5;
        }

        &__picture,
        &__image {
            display: block;

            width: 100%;
            height: 100%;

            object-fit: contain;
            object-position: center;
        }

        &__image--cut {
            object-fit: cover;
        }
    }
</style>
